import type { Movimiento, MovimientoConCliente } from '@/types/database'

// --- Types ---

export interface TotalesMovimientos {
  totalDebe: number
  totalHaber: number
  saldo: number
}

export interface AgingBucket {
  label: string
  dias: string
  monto: number
  color: string
}

export interface AgingData {
  buckets: AgingBucket[]
  total: number
}

// --- Sorting ---

/** Chronological sort comparator: fecha ASC, created_at ASC, id ASC */
export const sortCronologico = <T extends Pick<Movimiento, 'fecha' | 'created_at' | 'id'>>(a: T, b: T): number => {
  const fechaA = new Date(a.fecha).getTime()
  const fechaB = new Date(b.fecha).getTime()
  if (fechaA !== fechaB) return fechaA - fechaB
  const createdA = a.created_at ? new Date(a.created_at).getTime() : 0
  const createdB = b.created_at ? new Date(b.created_at).getTime() : 0
  if (createdA !== createdB) return createdA - createdB
  return a.id.localeCompare(b.id)
}

// --- Calculations ---

/** Simple totals: totalDebe, totalHaber, saldo (debe - haber).
 *  Uses _aplicacionSaldoPendiente when available (partial payments). */
export const calcularTotales = <T extends Pick<Movimiento, 'debe' | 'haber'> & { tipo?: string | null; _aplicacionSaldoPendiente?: number | null }>(
  movimientos: T[]
): TotalesMovimientos => {
  let totalDebe = 0
  let totalHaber = 0
  for (const mov of movimientos) {
    if (mov.tipo === 'Deuda' && mov._aplicacionSaldoPendiente != null) {
      totalDebe += mov._aplicacionSaldoPendiente
    } else {
      totalDebe += mov.debe ?? 0
    }
    if (mov.tipo === 'Pago' && mov._aplicacionSaldoPendiente != null) {
      totalHaber += mov._aplicacionSaldoPendiente
    } else {
      totalHaber += mov.haber ?? 0
    }
  }
  return { totalDebe, totalHaber, saldo: totalDebe - totalHaber }
}

/** Per-movement balance delta. In `neto` mode it uses _aplicacionSaldoPendiente
 *  (partial payments) for Deuda/Pago — same rule as calcularTotales — so the
 *  running balance matches the net saldo shown in the dialog/PDF. Otherwise
 *  it returns the gross debe - haber (default, used by the general Movimientos page). */
const deltaSaldo = (mov: MovimientoConCliente, neto: boolean): number => {
  if (!neto) return (mov.debe ?? 0) - (mov.haber ?? 0)
  const debe = mov.tipo === 'Deuda' && mov._aplicacionSaldoPendiente != null
    ? mov._aplicacionSaldoPendiente
    : (mov.debe ?? 0)
  const haber = mov.tipo === 'Pago' && mov._aplicacionSaldoPendiente != null
    ? mov._aplicacionSaldoPendiente
    : (mov.haber ?? 0)
  return debe - haber
}

/**
 * Calculate accumulated balance per movement, grouped by client.
 * Returns a Map<movimiento.id, saldoAcumulado>.
 *
 * Movements are sorted chronologically within each client group.
 * When `neto` is true, partial payments net the running balance (estado de cuenta);
 * the default (false) keeps the gross debe - haber used by the Movimientos page.
 */
export const calcularSaldosAcumulados = (
  movimientos: MovimientoConCliente[],
  getClienteKey?: (mov: MovimientoConCliente) => string,
  saldoInicial?: number,
  neto = false,
): Map<string, number> => {
  const saldoPorMovimiento = new Map<string, number>()

  // Group by client
  const porCliente = new Map<string, MovimientoConCliente[]>()
  for (const mov of movimientos) {
    const key = getClienteKey
      ? getClienteKey(mov)
      : mov.cliente_id ?? mov.cliente?.id ?? 'sin-cliente'
    const lista = porCliente.get(key) ?? []
    lista.push(mov)
    porCliente.set(key, lista)
  }

  for (const lista of porCliente.values()) {
    const ordenada = [...lista].sort(sortCronologico)
    let saldo = saldoInicial ?? 0
    for (const mov of ordenada) {
      saldo += deltaSaldo(mov, neto)
      saldoPorMovimiento.set(mov.id, saldo)
    }
  }

  return saldoPorMovimiento
}

// --- Aging ---

const AGING_BUCKETS = [
  { label: 'Corriente', dias: '0d', min: 0, max: 0, color: '#22c55e' },
  { label: '1-15d', dias: '1-15', min: 1, max: 15, color: '#eab308' },
  { label: '16-30d', dias: '16-30', min: 16, max: 30, color: '#f97316' },
  { label: '31-60d', dias: '31-60', min: 31, max: 60, color: '#ef4444' },
  { label: '60+', dias: '60+', min: 61, max: Infinity, color: '#991b1b' },
] as const

/**
 * Calculate aging buckets for unpaid debts.
 * Uses _aplicacionSaldoPendiente when available, falls back to debe.
 * Skips fully paid debts (saldo === 0).
 */
export const calcularAging = (
  movimientos: (Pick<Movimiento, 'tipo' | 'debe' | 'haber' | 'fecha'> & { _aplicacionSaldoPendiente?: number | null })[],
  fechaRef: Date = new Date(),
): AgingData => {
  const buckets: AgingBucket[] = AGING_BUCKETS.map(b => ({
    label: b.label,
    dias: b.dias,
    monto: 0,
    color: b.color,
  }))

  let total = 0

  for (const mov of movimientos) {
    if (mov.tipo !== 'Deuda') continue
    const monto = mov._aplicacionSaldoPendiente ?? mov.debe ?? 0
    if (monto <= 0) continue

    const fechaStr = mov.fecha.length === 10 ? `${mov.fecha}T00:00:00` : mov.fecha
    const diasVencido = Math.floor(
      (fechaRef.getTime() - new Date(fechaStr).getTime()) / (1000 * 60 * 60 * 24)
    )

    for (let i = 0; i < AGING_BUCKETS.length; i++) {
      if (diasVencido >= AGING_BUCKETS[i].min && diasVencido <= AGING_BUCKETS[i].max) {
        buckets[i].monto += monto
        total += monto
        break
      }
    }
  }

  return { buckets, total }
}
